import { ReactElement } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Heading, Link, List, Paragraph, Skeleton } from '@digdir/designsystemet-react';
import { ArrowLeftIcon } from '@navikt/aksel-icons';
import type { HmsSubject, SafetyRepRiskMatrixVersion, TemplateVersionDetail } from '../../api/hms';
import { Routes } from '../../enums/Routes';
import {
  useHmsSafetyRepRiskMatrixVersionQuery,
  useHmsSafetyRepTemplateVersionQuery,
} from '../../hooks/queries/hms/useHmsQueries';
import { formatDay } from '../HRDashboard/hmsFormat';
import { formatChange } from '../HRDashboard/HRRiskMatrix/riskMatrixView';
import { VersionDetails } from '../HRDashboard/HRRiskFactors/components/VersionView';
import { SafetyRepHero } from './components/SafetyRepHero';
import layout from '../HRDashboard/hmsPage.module.css';
import classes from './SafetyRep.module.css';

/** The version the safety representative is asked to approve, read-only */
export const SafetyRepVersion = ({ subject }: { subject: HmsSubject }): ReactElement => {
  const { t } = useTranslation();
  const id = Number(useParams<{ versionId: string }>().versionId);

  return (
    <div className={layout.page} data-size="md">
      <SafetyRepHero title={t('safetyRep.signing.title')} />
      <div className={classes.column}>
        <section aria-labelledby="vo-version" className={layout.card} data-gap="md">
          <Link asChild data-size="sm" className={classes.backLink}>
            <RouterLink to={Routes.SafetyRepSigning}>
              <ArrowLeftIcon aria-hidden />
              {t('safetyRep.version.back')}
            </RouterLink>
          </Link>
          {subject === 'riskMatrix' ? <RiskMatrixVersion id={id} /> : <TemplateVersion id={id} />}
        </section>
      </div>
    </div>
  );
};

const Loading = () => (
  <div aria-busy="true">
    <Skeleton variant="rectangle" width="100%" height={320} />
  </div>
);

const Failed = () => {
  const { t } = useTranslation();
  return (
    <Alert data-color="danger">
      <Paragraph data-size="sm">{t('safetyRep.version.error')}</Paragraph>
    </Alert>
  );
};

const Title = ({ subject, version, sentAt }: { subject: HmsSubject; version: number; sentAt: string }) => {
  const { t, i18n } = useTranslation();
  return (
    <div className={layout.cardHeading}>
      <Heading level={2} data-size="md" id="vo-version">
        {t(`safetyRep.subjects.${subject}`, { version })}
      </Heading>
      <Paragraph data-size="sm" className={layout.subtle}>
        {t('safetyRep.signing.from', {
          date: formatDay(sentAt, t, i18n.language),
        })}
      </Paragraph>
    </div>
  );
};

const RiskMatrixVersion = ({ id }: { id: number }) => {
  const { data, isLoading, error } = useHmsSafetyRepRiskMatrixVersionQuery(id);
  if (isLoading) return <Loading />;
  if (error || !data) return <Failed />;
  return <RiskMatrixChanges data={data} />;
};

const RiskMatrixChanges = ({ data }: { data: SafetyRepRiskMatrixVersion }) => {
  const { t } = useTranslation();
  const { version, factors } = data;
  const factorName = (key: string) =>
    factors.find((factor) => factor.key === key)?.name ?? t(`hrRiskMatrix.rules.factorNames.${key}`, key);
  return (
    <>
      <Title subject="riskMatrix" version={version.version} sentAt={version.submittedAt ?? version.createdAt} />
      {version.changeReason && (
        <div className={layout.cardHeading}>
          <Heading level={3} data-size="2xs">
            {t('safetyRep.version.reason')}
          </Heading>
          <Paragraph data-size="sm">{version.changeReason}</Paragraph>
        </div>
      )}
      <div className={layout.cardHeading}>
        <Heading level={3} data-size="2xs">
          {t('safetyRep.version.changedFrom', {
            version: data.current.version,
          })}
        </Heading>
        <List.Unordered data-size="sm">
          {version.changes.map((change) => {
            const text = formatChange(change, t, factorName);
            return <List.Item key={text}>{text}</List.Item>;
          })}
        </List.Unordered>
      </div>
    </>
  );
};

const TemplateVersion = ({ id }: { id: number }) => {
  const { data, isLoading, error } = useHmsSafetyRepTemplateVersionQuery(id);
  if (isLoading) return <Loading />;
  if (error || !data) return <Failed />;
  return <TemplateChanges data={data} />;
};

const TemplateChanges = ({ data }: { data: TemplateVersionDetail }) => (
  <>
    <Title subject="template" version={data.version} sentAt={data.submittedAt ?? data.createdAt} />
    <VersionDetails version={data} />
  </>
);
